"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
export default function TheExpertBehindTheStrategy() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1,
    once: true
  });
  const {
    ref: photoRef,
    isVisible: photoVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1,
    once: true
  });
  const {
    ref: storyRef,
    isVisible: storyVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1,
    once: true
  });
  const {
    ref: signRef,
    isVisible: signVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1,
    once: true
  });
  const {
    ref: faqRef,
    isVisible: faqVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1,
    once: true
  });
  const storyChapters = [{
    title: "Fixing Pricing Leaks Where They Actually Happen",
    text: "I founded HotelRevBaBa in 2019 to give independent hoteliers direct access to senior commercial guidance. Most hotels lose margin not because of low demand, but because their extranets are misconfigured, discounts stack unnoticed, and inventory sells out too cheaply on peak weekends. I log into your channel manager and extranets myself to plug those gaps."
  }, {
    title: "Direct Extranet Audits and Channel Management",
    text: "When you work with HotelRevBaBa, you work directly with me. I do not delegate your hotel to junior account managers. I audit your rate parity, commission tiers, and cancellation policies across Booking.com, Agoda, and MakeMyTrip, ensuring your inventory yields fair returns on every room night."
  }, {
    title: "Monitoring Pickup Pace and Lead Times",
    text: "Every morning, I review pickup pace, cancellation rates, and market compression across our portfolio. By tracking competitor rates and forward-looking demand, we adjust rates in real time instead of waiting for month-end reports when revenue is already lost."
  }, {
    title: "Minimum Length of Stay (MLOS) and Inventory Controls",
    text: "Protecting high-demand dates requires strict inventory controls. I implement strategic minimum length of stay restrictions and room-type differentials so your suites and prime weekends do not sell out at bargain rates to one-night stays."
  }, {
    title: "A Focused 14-Day Revenue Audit",
    text: "Every partnership begins with a thorough 14-day audit. I review your past performance, OTA commission tiers, direct booking engine conversion, and local compset pricing. This delivers a concrete action plan designed to produce a 15–25% net revenue improvement."
  }, {
    title: "Proven Results Across 50+ Independent Properties",
    text: "Over the past 15+ years, I have worked with more than 50 independent properties, boutique resorts, and homestays. My focus remains constant: protecting your net profit, reducing dependence on high-commission channels, and keeping your cash flow healthy."
  }];
  const faqs = [{
    question: "What is Sunil's revenue management background?",
    answer: "Sunil Singh brings over 15 years of hospitality commercial experience, holding a BA in Commerce alongside certifications from IHG, HSMAI, Google, Simplilearn, and Rate Buckets. He has guided revenue strategy for more than 50 independent hotels and boutique properties."
  }, {
    question: "Which markets does he serve?",
    answer: "Based in Surat, India, Sunil works directly with independent hotel owners, boutique resorts, and leisure properties across major Indian hospitality markets as well as select international destinations."
  }, {
    question: "How does he stay updated on OTA trends?",
    answer: "Sunil actively manages live extranet portals daily, monitoring OTA policy shifts, commission tiers, rate parity rules, and distribution updates across Booking.com, Agoda, MakeMyTrip, and global distribution channels."
  }];
  return <section data-section-id="3374" id="the-expert-behind-the-strategy" className="bg-white text-foreground relative overflow-hidden py-12">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        "name": "Sunil Singh",
        "jobTitle": "Hotel Revenue Management Consultant",
        "birthPlace": "India",
        "description": "Sunil Singh is an independent hotel revenue management consultant with 15+ years of experience helping hotels optimize OTA distribution, room pricing, and net profitability.",
        "image": "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunilsingh-image.jpg",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Surat",
          "addressCountry": "India"
        },
        "knowsAbout": ["Hotel revenue management", "OTA distribution", "Yield optimization", "Demand forecasting", "Extranet management"],
        "sameAs": ["https://www.linkedin.com/in/sunilsinghhospitality/", "https://twitter.com/hotelrevbaba"]
      })
    }} />
      
      <div className="pointer-events-none absolute top-0 right-0 h-[700px] w-[700px] -translate-y-1/4 translate-x-1/4 opacity-40" style={{
      background: "radial-gradient(circle, hsla(43, 46%, 55%, 0.25) 0%, hsla(43, 46%, 55%, 0.08) 35%, transparent 70%)"
    }} aria-hidden="true" />

      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div ref={headlineRef} className={`mb-6 transition-all duration-1000 ease-out ${headlineVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <div className="mb-6 flex justify-center">
            <span className="font-mono text-xs tracking-[0.2em] text-secondary uppercase">
              // EST. 2019 — VISION
            </span>
          </div>

          <h1 className="font-sans font-bold text-center text-3xl md:text-[48px] leading-tight tracking-tight text-[#0B4D9C]">
            Sunil Singh | Hospitality Revenue Consultant &amp; OTA Distribution Strategist
          </h1>

          <h2 className="mt-4 mx-auto max-w-3xl text-center font-sans font-semibold text-xl md:text-2xl text-[#0B4D9C]">
            Hands-On Yield Management, Extranet Parity &amp; Direct Booking Strategy
          </h2>

          <p className="mt-6 mx-auto max-w-3xl text-center font-sans text-[17px] leading-[1.7] text-[#0B4D9C]">
            With over 15 years of active commercial strategy and extranet management across boutique hotels, city properties, and leisure resorts, I work directly with property owners and general managers to fix hidden rate leakage, optimize OTA distribution, and grow net RevPAR.
          </p>

          <div className="mt-6 flex justify-center">
            <span className="font-mono text-[11px] tracking-[0.15em] text-muted-foreground uppercase">
              — From the desk of Sunil Singh
            </span>
          </div>
        </div>

        <div className="mb-6 h-px w-full bg-border" aria-hidden="true" />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div ref={photoRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${photoVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
            <div className="relative lg:sticky lg:top-24">
              <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-lg bg-[#0B4D9C] shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunilsingh-image.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunilsingh-image.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/sunilsingh-image.jpg-wvc-sizes" alt="Sunil Singh, hotel revenue management consultant" className="h-full w-full object-cover" loading="lazy" />
                
                <div className="pointer-events-none absolute inset-0 opacity-20 mix-blend-overlay" style={{
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
              }} aria-hidden="true" />
                <div className="pointer-events-none absolute inset-0" style={{
                background: "linear-gradient(180deg, hsla(222, 47%, 7%, 0.15) 0%, hsla(222, 47%, 7%, 0.25) 100%)"
              }} aria-hidden="true" />
              </div>

              <div className="mt-5 flex items-start justify-between gap-4">
                <span className="font-mono text-[10px] tracking-[0.18em] text-muted-foreground uppercase">
                  FIG. 01 / Sunil Singh — Principal Consultant
                </span>
                <span className="font-mono text-[10px] tracking-[0.18em] text-secondary uppercase">
                  ACTIVE
                </span>
              </div>
            </div>
          </div>

          <div ref={storyRef} className={`lg:col-span-7 transition-all duration-1000 ease-out lg:pl-4 ${storyVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`} style={{
          transitionDelay: "150ms"
        }}>
            <div className="mb-6 flex items-center gap-4">
              <span className="h-px w-10 bg-secondary" aria-hidden="true" />
              <span className="font-mono text-[11px] tracking-[0.2em] text-secondary uppercase">
                Chapter 01 / Philosophy
              </span>
            </div>

            {storyChapters.map((chapter, i) => <div key={i} data-index={i} className="mb-8">
                <h3 className="mb-3 font-sans text-[22px] font-semibold text-[#0B4D9C]">
                  {chapter.title}
                </h3>
                <p className="text-[16px] leading-[1.7] text-[#0B4D9C] font-sans font-normal">
                  {i === 0 && <span className="float-left mr-3 font-sans font-bold text-[96px] leading-[0.85] text-secondary" style={{
                marginTop: "0.05em"
              }}>
                      I
                    </span>}
                  {i === 0 ? chapter.text.slice(1) : chapter.text}
                </p>
                
                {i === 1 && <blockquote className="my-8 border-l-2 border-secondary pl-8">
                    <p className="font-sans font-normal text-[24px] md:text-[28px] leading-[1.3] italic text-[#0B4D9C]">
                      "True revenue management is hands-on work. It requires checking extranets every morning, auditing rate parity, and adjusting restrictions before bookings slip away."
                    </p>
                  </blockquote>}

                {i === 3 && <blockquote className="my-8 border-l-2 border-secondary pl-8">
                    <p className="font-sans font-normal text-[24px] md:text-[28px] leading-[1.3] italic text-[#0B4D9C]">
                      "Protecting peak dates with length-of-stay controls ensures your highest-value nights aren't sold out at low rates months in advance."
                    </p>
                  </blockquote>}
              </div>)}

            <div ref={faqRef} className={`mt-12 mb-12 transition-all duration-1000 ease-out ${faqVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
              <h2 className="mb-6 font-sans text-[28px] font-bold text-[#0B4D9C]">
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-b border-border">
                    <AccordionTrigger className="font-sans text-[18px] font-semibold text-[#0B4D9C] hover:text-secondary py-4 text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="font-sans text-[16px] leading-relaxed text-[#0B4D9C]/80 pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            <div ref={signRef} className={`mt-6 border-t border-border pt-6 transition-all duration-1000 ease-out ${signVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
              <div className="mb-6 flex items-center gap-4">
                <span className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground uppercase">
                  // Signed
                </span>
                <span className="h-px flex-1 bg-border" aria-hidden="true" />
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-1">
                {[{
                name: "Sunil Singh",
                title: "Founder & Principal Consultant",
                path: "M5 35 Q15 10, 30 25 T55 20 Q70 15, 85 30 T115 25"
              }].map((founder, i) => <div key={i} data-index={i} className="flex flex-col">
                    <svg viewBox="0 0 125 45" className="mb-3 h-12 w-auto" aria-hidden="true">
                      <path d={founder.path} stroke="var(--secondary)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    </svg>
                    <Link to="https://sunilsingh.co.in/" newTab className="font-sans font-normal text-[17px] text-[#0B4D9C] hover:text-secondary transition-colors">
                      {founder.name}
                    </Link>
                    <span className="mt-1 font-mono text-[10px] tracking-[0.15em] text-muted-foreground uppercase">
                      {founder.title}
                    </span>
                    <span className="mt-1 font-mono text-[10px] tracking-[0.15em] text-muted-foreground uppercase">
                      6 Professional Certifications | 15+ Years Hospitality Experience
                    </span>
                    <span className="mt-1 font-mono text-[10px] tracking-[0.15em] text-muted-foreground uppercase">
                      BA in Commerce | IHG, HSMAI, Google, Simplilearn, Rate Buckets Certified
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}